import React from 'react';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useTranslation } from 'react-i18next';
import CommonToolbarWithSearchBar from './CommonToolbarWithSearchBar';
import * as securitySlice from '../../../data/security.slice';
import * as lookupSlice from '../../../data/lookup.slice';
import RoleDataTable from './RoleDataTable';
import RoleDialog from './RoleDialog';
import { UserRole } from '../../../data/models/user-role.model';
import ConfirmationDialog from '../../ConfirmationDialog';
import { useIsSuperReaderUser } from '../../../hooks/permissions/useIsSuperReaderUser';
import { GridSortModel } from '@mui/x-data-grid-pro';

const PREFIX = 'Roles';

const classes = {
  container: `${PREFIX}-container`,
};

const StyledBox = styled(Box)(() => ({
  [`&.${classes.container}`]: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
}));

enum RoleExportColumn {
  Name = 'Role Name',
  Description = 'Description',
  Application = 'Application',
  UserCategory = 'User Category',
  IsDefault = 'Is Default',
  IsActive = 'Is Active',
}

interface RoleExportRow {
  [RoleExportColumn.Name]: string;
  [RoleExportColumn.Description]: string;
  [RoleExportColumn.Application]: string;
  [RoleExportColumn.UserCategory]: string;
  [RoleExportColumn.IsDefault]: string;
  [RoleExportColumn.IsActive]: string;
}

function mapRoleToExport(
  role: UserRole,
  categoryTypes: any[],
  applications: any[],
  t: (key: string) => string,
) {
  const categoryType =
    categoryTypes.find(
      (ct) => ct.userCategoryTypeID === role.userCategoryTypeID,
    )?.userCategoryTypeName ?? '';

  const application =
    applications.find(
      (app) => app.applicationTitleID === role.applicationTitleID,
    )?.applicationName ?? '';

  return {
    [RoleExportColumn.Name]: role.userRoleBC ?? '',
    [RoleExportColumn.Description]: role.description ?? '',
    [RoleExportColumn.Application]: application,
    [RoleExportColumn.UserCategory]: categoryType,
    [RoleExportColumn.IsDefault]: role.isDefault ? 'True' : 'False',
    [RoleExportColumn.IsActive]:
      role.userRoleState === 'active' ? 'True' : 'False',
  };
}

export default function Roles() {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const readOnly = false;
  const isSuperReaderUser = useIsSuperReaderUser();

  const loadingUserRoles = useAppSelector(securitySlice.selectLoadingUserRoles);
  const userRoles = useAppSelector(securitySlice.selectUserRoles);
  const applications = useAppSelector(lookupSlice.selectApplicationTitles);
  const categoryTypes = useAppSelector(lookupSlice.selectUserCategoryTypes);
  const groups = useAppSelector(securitySlice.selectUserGroups);
  const loadingAllRoles = useAppSelector(securitySlice.selectLoadingAllRoles);
  const loadedAllRoles = useAppSelector(securitySlice.selectLoadedAllRoles);
  const allRoles = useAppSelector(securitySlice.selectAllRoles);

  const [confirmDialogOpen, setConfirmDialogOpen] = React.useState(false);
  const [confirmDeleteMessage, setConfirmDeleteMessage] = React.useState('');
  const [roleToDelete, setRoleToDelete] = React.useState<UserRole>();

  const [editorOpen, setEditorOpen] = React.useState<boolean>(false);

  const [role, setRole] = React.useState<UserRole>();
  const [searchText, setSearchText] = React.useState('');
  const [sortModel, setSortModel] = React.useState<GridSortModel>([
    { field: 'description', sort: 'asc' },
  ]);
  const sortModelMemo = React.useMemo(() => {
    const sort = sortModel[0];
    return sort
      ? { column: sort.field, direction: sort.sort as 'asc' | 'desc' }
      : null;
  }, [sortModel]);

  React.useEffect(() => {
    const sort = sortModel[0]
      ? {
          column: sortModel[0].field,
          direction: sortModel[0].sort as 'asc' | 'desc',
        }
      : null;
    dispatch(
      securitySlice.fetchUserRolesAsync({
        searchText: searchText,
        sortModel: sort,
      }),
    );
  }, [dispatch, searchText, sortModel]);

  React.useEffect(() => {
    dispatch(securitySlice.fetchAllRolesForExportAsync('', sortModelMemo));
  }, [dispatch, sortModelMemo]);

  React.useEffect(() => {
    dispatch(securitySlice.fetchUserGroupsAsync());
    dispatch(lookupSlice.fetchLookupItemsAsync('applicationTitle'));
    dispatch(lookupSlice.fetchLookupItemsAsync('userCategoryType'));
  }, [dispatch]);

  const addRole = () => {
    setRole(undefined);
    setEditorOpen(true);
  };

  const editRole = (id: number) => {
    const role = userRoles.find((g) => g.userRoleID === id);
    if (role) {
      setRole(role);
      setEditorOpen(true);
    }
  };

  const deleteRole = (id: number) => {
    const role = userRoles.find((g) => g.userRoleID === id);
    if (role) {
      const name = role.userRoleName;
      const msg = t('roles.confirmDeleteMessage', { name });
      setConfirmDeleteMessage(msg);
      setRoleToDelete(role);
      setConfirmDialogOpen(true);
    }
  };

  const confirmDelete = () => {
    if (roleToDelete) {
      dispatch(securitySlice.deleteUserRoleAsync(roleToDelete.userRoleID!));
    }
    setConfirmDialogOpen(false);
  };

  const cancelDelete = () => {
    setConfirmDialogOpen(false);
  };

  const closeRoleEditor = (saved: boolean) => {
    setEditorOpen(false);
    setRole(undefined);
  };

  const onSearchTextChange = (val: string) => {
    setSearchText(val);
  };

  const handleSortModelChange = (model: GridSortModel) => {
    setSortModel(model);
  };

  const exportDisplayedRoles = userRoles.map((role) =>
    mapRoleToExport(role, categoryTypes, applications, t),
  );

  const fetchAllRoles = React.useCallback(() => {
    if (loadedAllRoles && !loadingAllRoles) {
      return (allRoles ?? []).map((role: UserRole) =>
        mapRoleToExport(role, categoryTypes, applications, t),
      );
    }
    return [];
  }, [
    loadedAllRoles,
    loadingAllRoles,
    allRoles,
    categoryTypes,
    applications,
    t,
  ]);

  return (
    <StyledBox className={classes.container}>
      <CommonToolbarWithSearchBar
        label={t('roles.search')}
        placeholder={t('roles.searchPlaceholder')}
        button={readOnly || isSuperReaderUser ? null : t('roles.add')}
        onCreate={addRole}
        onSearchTextChange={onSearchTextChange}
        searchText={searchText}
        exportDataMap={{
          [t('roles.displayedRoles')]: exportDisplayedRoles,
          [t('roles.allRoles')]: fetchAllRoles,
        }}
        exportFilenamePrefixMap={{
          [t('roles.displayedRoles')]: t('roles.displayedRoles'),
          [t('roles.allRoles')]: t('roles.allRoles'),
        }}
        disabled={loadingAllRoles}
      />
      <RoleDataTable
        roles={userRoles}
        groups={groups}
        loading={loadingUserRoles}
        readOnly={readOnly}
        onEdit={editRole}
        onDelete={deleteRole}
        onSortModelChange={handleSortModelChange}
        sortModel={sortModel}
      />
      <RoleDialog
        open={editorOpen}
        role={role}
        applications={applications}
        categoryTypes={categoryTypes}
        groups={groups}
        close={closeRoleEditor}
      />
      <ConfirmationDialog
        title={t('roles.deleteRole')}
        message={confirmDeleteMessage}
        open={confirmDialogOpen}
        positiveAction={confirmDelete}
        negativeAction={cancelDelete}
      />
    </StyledBox>
  );
}
